import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import {
  SupportTicket,
  SupportTicketCategory,
  useSendMessage,
  useUpdateTicket,
} from '@/hooks/useSupportChat';

interface Props {
  ticket: SupportTicket;
}

type Step = 'greeting' | 'category' | 'context' | 'description' | 'done';

const CATEGORIES: { code: SupportTicketCategory; labelKey: string }[] = [
  { code: 'offer', labelKey: 'supportChat.cat.offer' },
  { code: 'mission', labelKey: 'supportChat.cat.mission' },
  { code: 'account', labelKey: 'supportChat.cat.account' },
  { code: 'payment', labelKey: 'supportChat.cat.payment' },
  { code: 'other', labelKey: 'supportChat.cat.other' },
];

/** Machine à états qui pilote le ticket tant qu'il est en statut 'bot'. */
export const BotFlow = ({ ticket }: Props) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const sendMessage = useSendMessage();
  const updateTicket = useUpdateTicket();
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);

  const [contextSkipped, setContextSkipped] = useState(false);

  // Détermine l'étape courante à partir de l'état du ticket
  const step: Step = !ticket.category
    ? 'category'
    : (ticket.category === 'offer' || ticket.category === 'mission') &&
      !ticket.related_order_id &&
      !ticket.related_bid_id &&
      !contextSkipped
    ? 'context'
    : !ticket.subject
    ? 'description'
    : 'done';

  // Envoie un message de bienvenue à l'ouverture du ticket
  useEffect(() => {
    if (ticket.status !== 'bot') return;
    (async () => {
      const { count } = await supabase
        .from('support_messages')
        .select('id', { count: 'exact', head: true })
        .eq('ticket_id', ticket.id);
      if ((count ?? 0) === 0) {
        await sendMessage.mutateAsync({
          ticketId: ticket.id,
          content: t('supportChat.bot.greeting'),
          senderType: 'bot',
        });
        await sendMessage.mutateAsync({
          ticketId: ticket.id,
          content: t('supportChat.bot.askCategory'),
          senderType: 'bot',
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket.id]);

  // Liste les orders pour contexte
  const { data: orders } = useQuery({
    queryKey: ['support-context-orders', user?.id, ticket.category],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('id, order_number, pickup_city, delivery_city, status')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return data || [];
    },
    enabled: step === 'context',
  });

  const pickCategory = async (cat: SupportTicketCategory) => {
    if (busy) return;
    setBusy(true);
    await sendMessage.mutateAsync({
      ticketId: ticket.id,
      content: t(CATEGORIES.find((c) => c.code === cat)!.labelKey),
      senderType: 'user',
      metadata: { kind: 'category', value: cat },
    });
    await updateTicket.mutateAsync({ id: ticket.id, patch: { category: cat } });
    if (cat === 'offer' || cat === 'mission') {
      await sendMessage.mutateAsync({
        ticketId: ticket.id,
        content: t('supportChat.bot.askContext'),
        senderType: 'bot',
      });
    } else {
      await sendMessage.mutateAsync({
        ticketId: ticket.id,
        content: t('supportChat.bot.askDescription'),
        senderType: 'bot',
      });
    }
    setBusy(false);
  };

  const pickOrder = async (orderId: string, label: string) => {
    if (busy) return;
    setBusy(true);
    await sendMessage.mutateAsync({
      ticketId: ticket.id,
      content: label,
      senderType: 'user',
      metadata: { kind: 'order', value: orderId },
    });
    await updateTicket.mutateAsync({
      id: ticket.id,
      patch: { related_order_id: orderId },
    });
    await sendMessage.mutateAsync({
      ticketId: ticket.id,
      content: t('supportChat.bot.askDescription'),
      senderType: 'bot',
    });
    setBusy(false);
  };

  const skipContext = async () => {
    if (busy) return;
    setBusy(true);
    await sendMessage.mutateAsync({
      ticketId: ticket.id,
      content: t('supportChat.skip'),
      senderType: 'user',
    });
    // Truc : on met un subject vide pour avancer sans related_order
    await updateTicket.mutateAsync({
      id: ticket.id,
      patch: { related_order_id: null, related_bid_id: null },
    });
    await sendMessage.mutateAsync({
      ticketId: ticket.id,
      content: t('supportChat.bot.askDescription'),
      senderType: 'bot',
    });
    setContextSkipped(true);
    setBusy(false);
  };

  const submitDescription = async () => {
    if (busy || !description.trim()) return;
    setBusy(true);
    await sendMessage.mutateAsync({
      ticketId: ticket.id,
      content: description.trim(),
      senderType: 'user',
    });
    await updateTicket.mutateAsync({
      id: ticket.id,
      patch: { subject: description.trim().slice(0, 120), status: 'pending_admin' },
    });
    await sendMessage.mutateAsync({
      ticketId: ticket.id,
      content: t('supportChat.bot.handoff'),
      senderType: 'bot',
    });
    setDescription('');
    setBusy(false);
  };

  if (ticket.status !== 'bot') return null;

  return (
    <div className="border-t border-[#DBE2EA] bg-[#F8F9FA] p-4">
      {step === 'category' && (
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <Button
              key={c.code}
              variant="outline"
              size="sm"
              className="border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] rounded-lg"
              onClick={() => pickCategory(c.code)}
              disabled={busy}
            >
              {t(c.labelKey)}
            </Button>
          ))}
        </div>
      )}

      {step === 'context' && (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          <p className="text-xs text-[#5A6B7A] mb-2">{t('supportChat.pickOrder')}</p>
          {(orders || []).map((o: any) => (
            <Button
              key={o.id}
              variant="outline"
              size="sm"
              className="w-full justify-start text-left border-[#DBE2EA] hover:bg-[#EBF4F8] rounded-lg"
              onClick={() =>
                pickOrder(
                  o.id,
                  `${o.order_number} — ${o.pickup_city || '?'} → ${o.delivery_city || '?'}`
                )
              }
              disabled={busy}
            >
              <span className="truncate">
                {o.order_number} — {o.pickup_city || '?'} → {o.delivery_city || '?'}
              </span>
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-[#5A6B7A]"
            onClick={skipContext}
            disabled={busy}
          >
            {t('supportChat.skip')}
          </Button>
        </div>
      )}

      {step === 'description' && (
        <div className="space-y-2">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('supportChat.descPlaceholder')}
            rows={3}
            className="border-[#DBE2EA] focus:border-[#0880AE] rounded-lg resize-none"
          />
          <Button
            onClick={submitDescription}
            disabled={busy || !description.trim()}
            className="w-full bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] rounded-lg"
          >
            {t('supportChat.send')}
          </Button>
        </div>
      )}
    </div>
  );
};
